import type { ApprovalProposalListItem } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { ApprovalStatusBadge } from "@/components/approval/approval-status-badge";
import { ProposalExpiry } from "@/components/approval/proposal-expiry";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { TableCell, TableRow } from "@/components/ui/table";
import { useFormatters } from "@/lib/format";
import { localizedTargetLabel } from "@/lib/target-display";

interface ApprovalProposalRowsProps {
  proposal: ApprovalProposalListItem;
  now: Date;
  onReview: () => void;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
}

/**
 * ADP-127: 展开机制已移除——单动作提案(常态)的竞价变更直接放在行上;
 * 多动作提案显示变更数,逐条细节在决策详情页审核。
 */
export function ApprovalProposalRows({
  proposal,
  now,
  onReview,
  selected,
  onSelectedChange,
}: ApprovalProposalRowsProps) {
  const { t } = useTranslation("approval");
  const { formatCurrency, formatDateTime } = useFormatters();
  const money = (value: number | null): string => (value === null ? "—" : formatCurrency(value));
  const singleAction = proposal.actions.length === 1 ? proposal.actions[0] : undefined;
  return (
    <TableRow>
      {onSelectedChange ? (
        <TableCell className="w-10">
          <Checkbox
            aria-label={t("table.selectProposal", { name: proposal.campaignName })}
            checked={selected}
            onCheckedChange={(checked) => onSelectedChange(checked === true)}
          />
        </TableCell>
      ) : null}
      <TableCell>
        <div className="flex min-w-56 flex-col gap-0.5">
          <span className="font-medium text-foreground">{proposal.campaignName}</span>
          <span className="font-mono text-xs text-muted-foreground">
            {/* 多店范围下标注来源店铺,消除同名广告活动歧义 */}
            {proposal.shopName ? `${proposal.shopName} · ` : ""}
            {proposal.runId.slice(0, 8)}
          </span>
        </div>
      </TableCell>
      <TableCell>
        {proposal.managedGroupId ? (
          <span className="text-xs text-foreground">
            {proposal.managedGroupName ?? proposal.managedGroupId.slice(0, 8)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{t("table.ungrouped")}</span>
        )}
      </TableCell>
      <TableCell>
        {singleAction ? (
          <div className="flex flex-col gap-0.5">
            <span>
              {t(`actionType.${singleAction.actionType}`)}
              <span className="text-muted-foreground">
                {" "}
                · {localizedTargetLabel(singleAction.targetLabel, t)}
              </span>
            </span>
            <span className="font-mono text-xs tabular-nums text-muted-foreground">
              {money(singleAction.oldBid)} → {money(singleAction.newBid)}
            </span>
          </div>
        ) : (
          <span>{t("table.multiActions", { count: proposal.actionCount })}</span>
        )}
      </TableCell>
      <TableCell>
        {proposal.highestRisk ? (
          <ApprovalStatusBadge value={proposal.highestRisk} />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="font-mono text-xs tabular-nums text-muted-foreground">
        {formatDateTime(proposal.createdAt)}
      </TableCell>
      <TableCell className="text-right">
        <ProposalExpiry expiresAt={proposal.expiresAt} now={now} />
      </TableCell>
      <TableCell>
        <Button variant="outline" size="xs" onClick={onReview}>
          {t("table.review")}
        </Button>
      </TableCell>
    </TableRow>
  );
}
